import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Plus } from "lucide-react";
import { Reveal, MaskedLine, EASE } from "./Reveal";

const FAQS = [
  {
    q: "Hur fungerar flyttbidraget från Umeå universitet?",
    a: "Du som blir utplacerad via läkarprogrammet (T6) får besked om ditt flyttbidrag av universitetet, ofta redan under T4. Beloppet beror på vilken ort du placerats i – Östersund, Sundsvall eller Luleå. Vi skickar offert och underlag som universitetet behöver, och fakturerar direkt enligt ditt bidragsbeslut.",
  },
  {
    q: "Betalar jag något själv som student?",
    a: "Vid utplacering via universitetet skickar vi fakturan direkt till din skola – du betalar ingenting ur egen ficka. Flyttar du som student utan utplacering får du alltid fast pris i förväg, studentanpassat och prisvärt.",
  },
  {
    q: "Vad är RUT-avdrag och hur funkar det?",
    a: "Som privatperson har du rätt till 30 % RUT-avdrag på arbetskostnaden för flyttjänster. Vi sköter hela administrationen med Skatteverket – avdraget syns direkt på din faktura.",
  },
  {
    q: "Är mina saker försäkrade under flytten?",
    a: "Ja. Vi har ansvarsförsäkring som täcker din egendom under både flytt och transport, så att din flytt sker tryggt och säkert från dörr till dörr.",
  },
  {
    q: "Hur tidigt behöver jag boka?",
    a: "Så fort du fått ditt utplaceringsbesked eller vet ditt datum – ju tidigare desto bättre, särskilt inför terminsstart när många flyttar samtidigt. Vi återkommer alltid med offert så snart vi kan.",
  },
  {
    q: "Vad händer om mitt flyttdatum ändras?",
    a: "Lugnt – utplaceringsbesked och datum kan ändras. Hör av dig så snart du vet så bokar vi om utan krångel. Vi är vana vid att anpassa oss efter universitetets schema.",
  },
];

const FaqItem = ({ item, index }) => {
  const [open, setOpen] = useState(index === 0);
  return (
    <div data-testid={`faq-item-${index}`} className="border-b border-slate-200">
      <button
        data-testid={`faq-question-${index}`}
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between gap-6 py-6 text-left group"
      >
        <span className="font-display font-bold text-lg sm:text-xl text-[#0b0f19] group-hover:text-[#16a34a] transition-colors duration-300">
          {item.q}
        </span>
        <motion.span
          animate={{ rotate: open ? 45 : 0 }}
          transition={{ duration: 0.3, ease: EASE }}
          className={`w-9 h-9 shrink-0 rounded-full border flex items-center justify-center transition-colors duration-300 ${open ? "bg-[#16a34a] border-[#16a34a] text-white" : "border-slate-300 text-slate-500"}`}
        >
          <Plus className="w-4 h-4" />
        </motion.span>
      </button>
      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            data-testid={`faq-answer-${index}`}
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.4, ease: EASE }}
            className="overflow-hidden"
          >
            <p className="pb-7 pr-14 text-sm sm:text-base leading-relaxed text-slate-600 max-w-3xl">{item.a}</p>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export const FAQSection = () => (
  <section id="faq" data-testid="faq-section" className="bg-[#fafafa] py-24 sm:py-36 border-t border-slate-200">
    <div className="mx-auto max-w-7xl px-5 sm:px-8 grid lg:grid-cols-[1fr_1.6fr] gap-12 lg:gap-20">
      <div className="lg:sticky lg:top-28 self-start text-center lg:text-left">
        <Reveal>
          <p className="font-mono2 text-[11px] uppercase tracking-[0.3em] text-[#16a34a] mb-5">Vanliga frågor</p>
        </Reveal>
        <h2 className="font-display font-extrabold tracking-tight leading-[1.05] text-[clamp(1.9rem,8vw,3rem)] text-[#0b0f19]">
          <MaskedLine>Allt du</MaskedLine>
          <MaskedLine delay={0.1}>undrar över.</MaskedLine>
        </h2>
        <Reveal delay={0.2}>
          <p className="mt-6 text-base leading-relaxed text-slate-600 max-w-sm mx-auto lg:mx-0">
            Hittar du inte svaret? Ring oss på{" "}
            <a data-testid="faq-phone-link" href="tel:+46707272941" className="text-[#16a34a] font-semibold hover:text-[#15803d]">
              070-727 29 41
            </a>{" "}
            så hjälper vi dig direkt.
          </p>
        </Reveal>
      </div>
      <Reveal delay={0.1}>
        <div className="border-t border-slate-200">
          {FAQS.map((f, i) => (
            <FaqItem key={i} item={f} index={i} />
          ))}
        </div>
      </Reveal>
    </div>
  </section>
);
